<template>
  <div class="player-controls-container">
    <div class="play-control">
      <button
        @click="togglePlay"
        :aria-label="videoPlaying ? 'Pause' : 'Play'"
        class="pause-button pure-button video-control-button"
        tabindex="0"
      >
        <play-icon v-if="!videoPlaying"></play-icon>
        <pause-icon v-if="videoPlaying"></pause-icon>
      </button>
      <div class="timeline">
        <input
          :tabindex="disableVideoProgress ? '-1' : '0'"
          id="video-range-slider"
          type="range"
          role="progressbar"
          ref="videoProgressBar"
          class="progress-background icon-video-timeline focusable"
          :value="videoProgress"
          :aria-label="
            `${
              disableVideoProgress ? 'Static' : 'Adjustable'
            } video progress indicator - ${Math.round(videoProgress)}%`
          "
          @change="handleVideoSeek"
        />
      </div>
    </div>
    <div class="volume-control">
      <button
        @click="toggleAudio"
        :aria-label="videoMuted ? 'Unmute' : 'Mute'"
        class="volume-button pure-button video-control-button"
        tabindex="0"
      >
        <volume-icon height="40px" width="40px" v-if="!videoMuted"></volume-icon>
        <muted-icon height="40px" width="40px" v-if="videoMuted"></muted-icon>
      </button>
      <div class="volume-level-control">
        <volume-slider :sliderStyles="{ maxHeight: '9px', width: '100%' }"></volume-slider>
      </div>
    </div>
    <div class="cc-control">
      <div
        class="cc-size-control"
        :style="{ visibility: !hasCaptions || !captionsOn ? 'hidden' : 'visible' }"
      >
        <button
          @click="() => handleCCSizeChange(false)"
          aria-label="Decrease caption size"
          class="volume-button pure-button cc-control-button"
          tabindex="0"
        >
          <cc-down-icon></cc-down-icon>
        </button>
        <div class="cc-size-text" :aria-label="`Caption size - ${captionSizePercentage}`">
          {{ captionSizePercentage }}
        </div>
        <button
          @click="() => handleCCSizeChange(true)"
          aria-label="Increase caption size"
          class="volume-button pure-button cc-control-button"
          tabindex="0"
        >
          <cc-up-icon></cc-up-icon>
        </button>
      </div>
      <button
        @click="toggleCaptions"
        :aria-label="captionsOn ? 'Turn captions off' : 'Turn captions on'"
        class="volume-button pure-button video-control-button"
        tabindex="0"
        :disabled="!hasCaptions"
      >
        <cc-on-icon height="40px" width="40px" v-if="hasCaptions && !captionsOn"></cc-on-icon>
        <cc-off-icon height="40px" width="40px" v-if="hasCaptions && captionsOn"></cc-off-icon>
        <cc-disabled-icon height="40px" width="40px" v-if="!hasCaptions"></cc-disabled-icon>
      </button>
    </div>
    <div class="narration-control">
      <button
        @click="toggleVideoNarrationSettings"
        :aria-label="videoNarrationOn ? 'Turn video narration off' : 'Turn video narration on'"
        class="volume-button pure-button video-control-button"
        :disabled="!hasVideoNarration"
        tabindex="0"
      >
        <narration-on-icon
          height="40px"
          width="40px"
          v-if="hasVideoNarration && !videoNarrationOn"
        ></narration-on-icon>
        <narration-off-icon
          height="40px"
          width="40px"
          v-if="hasVideoNarration && videoNarrationOn"
        ></narration-off-icon>
        <narration-disabled-icon
          height="40px"
          width="40px"
          v-if="!hasVideoNarration"
        ></narration-disabled-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
import btnClose from 'assets/svg/btn-close.svg';
import PlayIcon from 'components/icons/VideoControls/PlayIcon.vue';
import PauseIcon from 'components/icons/VideoControls/PauseIcon.vue';
import CcUpIcon from 'components/icons/VideoControls/CCSizeUpIcon.vue';
import CcDownIcon from 'components/icons/VideoControls/CCSizeDownIcon.vue';
import CcOnIcon from 'components/icons/VideoControls/CCOnIcon.vue';
import CcOffIcon from 'components/icons/VideoControls/CCOffIcon.vue';
import CcDisabledIcon from 'components/icons/VideoControls/CCDisabledIcon.vue';
import MutedIcon from 'components/icons/VolumeControls/VolumeMutedIcon.vue';
import VolumeIcon from 'components/icons/VolumeControls/VolumeIcon.vue';
import VolumeSlider from 'components/inputs/VolumeSlider.vue';
import NarrationOffIcon from 'components/icons/VideoControls/NarrationOffIcon.vue';
import NarrationOnIcon from 'components/icons/VideoControls/NarrationOnIcon.vue';
import NarrationDisabledIcon from 'components/icons/VideoControls/NarrationDisabledIcon.vue';
import AccessibilityHelper from 'mixins/AccessibilityHelper';

export default {
  name: 'VideoPlayerControls',
  components: {
    PlayIcon,
    PauseIcon,
    CcOnIcon,
    CcOffIcon,
    CcUpIcon,
    CcDownIcon,
    CcDisabledIcon,
    MutedIcon,
    NarrationOffIcon,
    NarrationOnIcon,
    NarrationDisabledIcon,
    VolumeIcon,
    VolumeSlider
  },
  props: {
    video: {
      required: true
    },
    videoReference: {
      required: true
    }
  },
  computed: {
    ...mapState('application', {
      isMobile: state => state.device.device
    }),

    ...mapState('student', {
      accessibilitySettings: state => state.accessibilitySettings,
      captionsOn: state => state.accessibilitySettings.closedCaptionsSettings.showClosedCaptions,
      captionSize: state => state.accessibilitySettings.closedCaptionsSettings.closedCaptionSize,
      volume: state => state.accessibilitySettings.audioSettings.volume,
      videoMuted: state => state.accessibilitySettings.audioSettings.muteSounds,
      videoNarrationOn: state => state.accessibilitySettings.videoNarrationSettings.showSettings
    }),

    ...mapGetters('application', ['usingExternalKeyboard']),

    captionSizePercentage() {
      const percMap = {
        12: '50%',
        22: '100%',
        32: '150%',
        42: '200%'
      };
      return percMap[this.captionSize];
    },

    disableVideoProgress() {
      return this.video?.title === 'The Home Row';
    }
  },

  watch: {
    videoReference() {
      this.setTheVideo();
      // Fix for video captions not being displayed by default when captions settings are enabled
      this.maybeToggleCaptionsOn();
    },
    volume(newVolume) {
      this.videoRef.volume = newVolume * 0.01;
    },
    videoMuted(newIsMuted) {
      this.videoRef.muted = newIsMuted;
    },
    videoNarrationOn(isOn) {
      const source = document.getElementById('videoSource');

      if (!!source) {
        const { track, narrationTrack } = this.getTracks();

        source.setAttribute('src', isOn ? this.video.narrationSrc : this.video.src);
        track.mode = 'hidden';
        narrationTrack.mode = 'hidden';
        this.videoRef.load();
        this.videoRef.play();
        this.maybeToggleCaptionsOn();
      }
    }
  },

  data() {
    return {
      videoPlaying: false,
      videoDone: false,
      buttonClose: btnClose,
      videoRef: '',
      showCloseButton: true,
      hasCaptions: false,
      captionMin: 12,
      captionMax: 42,
      videoProgress: 0,
      hasVideoNarration: false
    };
  },
  methods: {
    ...mapMutations('application', ['setModal']),
    ...mapActions('student', ['saveStudentSettings']),
    ...mapMutations('student', ['setAccessibilitySettings']),

    updateTrack(track, id, videoVtt) {
      track.id = id;
      track.kind = 'captions';
      track.label = 'English';
      track.srclang = 'en';
      track.src = videoVtt;
      track.mode = 'hidden';

      return track;
    },

    getTracks() {
      const tracks = Array.from(this.videoRef.textTracks);
      return { track: tracks?.[0], narrationTrack: tracks?.[1] };
    },

    setTheVideo() {
      const video = this.video;

      const videoSrc = this.videoNarrationOn && video.narrationSrc ? video.narrationSrc : video.src;
      const videoVtt = this.videoNarrationOn && video.narrationVtt ? video.narrationVtt : video.vtt;

      const source = document.createElement('source');
      source.setAttribute('src', videoSrc);
      source.setAttribute('id', 'videoSource');

      if (!!videoVtt) this.hasCaptions = true;
      if (!!video.narrationSrc) this.hasVideoNarration = true;

      const newTrack = document.createElement('track');
      const newNarrationTrack = document.createElement('track');

      const track = this.updateTrack(newTrack, 'track', video.vtt);
      const narrationTrack = this.updateTrack(
        newNarrationTrack,
        'narrationTrack',
        video.narrationVtt
      );

      this.videoRef = this.videoReference;

      if (!this.videoRef) return;
      this.videoRef.appendChild(source);

      this.videoRef.appendChild(track);
      this.videoRef.appendChild(narrationTrack);

      this.videoRef.setAttribute('aria-label', video.description);
      this.videoRef.muted = this.videoMuted;
      this.videoRef.volume = this.volume * 0.01;
      this.setCueFontSize(this.captionSize);
      const promise = this.videoRef?.play();

      if (promise !== undefined) {
        promise
          .then(() => {
            // Play started, update play button
            this.videoPlaying = true;
          })
          .catch(() => {
            this.videoPlaying = false;
          });
      }

      this.videoRef.addEventListener('timeupdate', this.videoTimeUpdated, false);
    },

    closeModal() {
      this.pauseVideo();
      this.setModal(undefined);
    },

    togglePlay() {
      if (this.videoPlaying) {
        this.pauseVideo();
      } else {
        this.playVideo();
      }
    },

    toggleAudio() {
      this.videoRef.muted = !this.videoMuted;
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.audioSettings.muteSounds = !this.videoMuted;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
    },

    toggleCaptions() {
      if (this.captionsOn) {
        this.toggleCaptionsOff();
      } else {
        this.toggleCaptionsOn();
      }
    },

    toggleVideoNarrationSettings() {
      if (this.videoNarrationOn) {
        this.toggleVideoNarrationOff();
      } else {
        this.toggleVideoNarrationOn();
      }
    },

    pauseVideo() {
      this.videoPlaying = false;
      this.videoRef?.pause();
    },

    playVideo() {
      this.videoPlaying = true;
      this.videoRef?.play();
    },

    handleUpdatingAccessibilitySettings(clonedSettings) {
      this.setAccessibilitySettings(clonedSettings);
      this.saveStudentSettings();
    },

    toggleCaptionsOn() {
      const { track, narrationTrack } = this.getTracks();
      if (this.videoNarrationOn && narrationTrack) {
        narrationTrack.mode = 'showing';
        if (track) track.mode = 'hidden';
      } else if (track) {
        if (track) track.mode = 'showing';
        if (narrationTrack) narrationTrack.mode = 'hidden';
      }
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.closedCaptionsSettings.showClosedCaptions = true;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
    },

    toggleCaptionsOff() {
      const { track, narrationTrack } = this.getTracks();
      if (narrationTrack) narrationTrack.mode = 'hidden';
      if (track) track.mode = 'hidden';
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.closedCaptionsSettings.showClosedCaptions = false;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
    },

    toggleVideoNarrationOn() {
      const { track, narrationTrack } = this.getTracks();
      if (track) track.mode = 'hidden';
      if (narrationTrack && this.captionsOn) narrationTrack.mode = 'showing';
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.videoNarrationSettings.showSettings = true;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
    },

    toggleVideoNarrationOff() {
      const { track, narrationTrack } = this.getTracks();
      if (narrationTrack) narrationTrack.mode = 'hidden';
      if (track && this.captionsOn) track.mode = 'showing';
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.videoNarrationSettings.showSettings = false;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
    },

    async setCueFontSize(fontSize) {
      let styleSheet = document.getElementById('faux-style-sheet');
      if (styleSheet) styleSheet.remove();

      styleSheet = document.createElement('style');
      styleSheet.id = 'faux-style-sheet';
      document.head.appendChild(styleSheet);

      const cssText = `video::cue {font-size: ${fontSize}px;}`;

      styleSheet.sheet.insertRule(cssText, 0);
    },

    handleCCSizeChange(isIncrease) {
      if (!this.videoRef) return;

      const newFontSize = isIncrease ? this.captionSize + 10 : this.captionSize - 10;

      if (newFontSize > this.captionMax || newFontSize < this.captionMin) return;
      const clonedSettings = { ...this.accessibilitySettings };
      clonedSettings.closedCaptionsSettings.closedCaptionSize = newFontSize;
      this.handleUpdatingAccessibilitySettings(clonedSettings);
      this.setCueFontSize(newFontSize);
      this.setScreenReaderAlert(`Caption Size - ${this.captionSizePercentage}`);
    },

    handleVideoSeek(e) {
      if (this.disableVideoProgress) return;
      e.preventDefault();
      e.stopPropagation();
      this.videoEndCheck();
      const position = +e.target.value;
      const currentTime = position * 0.01 * this.videoRef.duration;
      this.videoRef.currentTime = currentTime;
      this.videoProgress = position;
    },

    videoTimeUpdated(e) {
      e.preventDefault();
      e.stopPropagation();
      this.videoEndCheck();
      this.videoProgress = (this.videoRef?.currentTime / this.videoRef?.duration) * 100;
    },

    videoEndCheck() {
      if (this.videoRef?.currentTime >= this.videoRef?.duration) {
        this.videoPlaying = false;
        this.videoDone = true;
        this.videoProgress = 100;
        this.videoRef?.removeEventListener('timeupdate', this.videoTimeUpdated, false);
        setTimeout(() => {
          this.setModal(undefined);
        }, 1500);
      }
    },

    maybeToggleCaptionsOn() {
      if (this.hasCaptions && this.captionsOn) {
        this.toggleCaptionsOn();
      }
    }
  },
  mixins: [AccessibilityHelper]
};
</script>

<style lang="scss" scoped>
button {
  padding: 0px;
}

#video-range-slider {
  -webkit-appearance: none;
  overflow: hidden;
  width: 100%;
  max-height: 13px;
  min-width: 40px;
  height: 25%;
  border-radius: 5px;
  border: 1px solid black;
  background: #dddddd;
  -webkit-transition: 0.2s;
  transition: opacity 0.2s;
  overflow: visible;
}

#video-range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 32px;
  height: 31px;
  max-height: 100%;
  max-width: 100%;
  cursor: pointer;
  background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2230%22%20height%3D%2232%22%20viewBox%3D%220%200%2030%2032%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M19.1636%201.72501C14.7455%201.89167%205.22182%202.42501%202.47273%203.22501C1.98182%205.72501%201%2012.025%201%2017.225C1%2022.425%201.98182%2027.3917%202.47273%2029.225L10.3273%2029.725L22.1091%2028.725L27.0182%2027.725L28%2023.725C28%2020.5583%2027.9018%2013.325%2027.5091%209.72501C27.0182%205.22501%2025.5455%203.22501%2025.0545%202.72501C24.6618%202.32501%2020.9636%201.89167%2019.1636%201.72501Z%22%20fill%3D%22white%22%2F%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M2.87798%202.00271C0.446975%203.23171%200.369975%203.59571%200.0989753%2015.2577C-0.271025%2031.1727%20-0.201025%2031.2687%2011.539%2030.9407C25.017%2030.5647%2027.843%2029.9317%2028.941%2027.0457C29.469%2025.6567%2029.59%2020.1617%2029.218%2014.4827C28.335%201.01771%2028.016%200.705708%2015.128%200.725708C9.28998%200.734708%204.37498%201.24671%202.87798%202.00271ZM25.307%204.28171C26.542%205.51771%2026.878%208.17371%2026.878%2016.7097V27.5667L21.628%2028.1317C18.74%2028.4427%2013.49%2028.8127%209.95998%2028.9537L3.54098%2029.2107L2.67298%2025.9277C2.19498%2024.1227%201.93298%2018.4977%202.09098%2013.4277L2.37798%204.21071L7.87798%203.62371C18.791%202.45871%2023.668%202.64371%2025.307%204.28171ZM7.60598%208.31571C6.56198%209.35971%206.70397%2024.7107%207.75797%2024.7107C8.84697%2024.7107%209.78098%2013.0607%208.94497%209.89871C8.60997%208.62771%208.00698%207.91471%207.60598%208.31571ZM13.585%2015.4067C12.991%2023.2537%2013.292%2025.1907%2014.947%2024.1677C16.115%2023.4457%2016.187%207.71071%2015.022%207.71071C14.552%207.71071%2013.905%2011.1737%2013.585%2015.4067ZM19.585%2015.4067C18.991%2023.2537%2019.292%2025.1907%2020.947%2024.1677C22.115%2023.4457%2022.187%207.71071%2021.022%207.71071C20.552%207.71071%2019.905%2011.1737%2019.585%2015.4067Z%22%20fill%3D%22black%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-size: 80% auto;
  background-position-y: 50%;
  background-position-x: 50%;

  @media (max-width: 432px) {
    width: 12px;
    height: 11px;
  }
}
</style>
